<template>
  <Wizzard
    ref="wizzardRef"
    name="Private Fund"
    :title
    :modal
    :preset
    :steps="STEPS_LIST"
    :transaction-type
    :asset-type="ASSET_TYPE"
    :asset-classes-list="ASSET_CLASSES_LIST"
    :investment-strategies-list
    :handle-before-save-transaction
    :handle-before-save-asset
    :handle-step-status
    @init="handleInit"
  >
    <template #step2="{ asset, transaction }">
      <PrivateFundInvestment
        v-model:is-no-drawdown="isNoDrawdown"
        :asset
        :transaction
      />
    </template>
    <template #step3="{ transaction }">
      <PrivateFundContributions
        v-model:is-no-drawdown="isNoDrawdown"
        :contributions="contributions as TransactionClass[]"
        :transaction
        @update:contributions="contributions = $event"
      />
    </template>
    <template #step4>
      <PrivateFundDistributions
        :distributions="distributions as TransactionClass[]"
        @update:distributions="distributions = $event"
      />
    </template>
  </Wizzard>
</template>

<script setup lang="ts">
import { computed, provide, ref, useTemplateRef, watch } from 'vue'

import { AssetClass, ModalClass, TransactionClass } from '@types'
import { InputWizardPreset } from '../utils/types'

import {
  ASSET_CLASSES_LIST,
  ASSET_TYPE,
  ASSET_TYPES_LIST,
  INVESTMENT_STRATEGIES_LIST,
  INVESTMENT_STRATEGIES_LIST_NO_DRAWDOWN,
  STEPS_LIST,
  TRANSACTION_TYPE,
  TRANSACTION_TYPE_NO_DRAWDOWN,
} from './utils/const'
import { TRANSACTION_FIELD } from '@/const'

import { addPostfixToAssetTicker } from '../utils/helpers'

import { useAssetsBunchStore } from '@/store/assets/bunch'

import Wizzard from '../components/Wizzard.vue'
import PrivateFundContributions from './PrivateFundContributions.vue'
import PrivateFundDistributions from './PrivateFundDistributions.vue'
import PrivateFundInvestment from './PrivateFundInvestment.vue'

type Props = {
  modal: ModalClass<any>
  preset?: InputWizardPreset
}

defineProps<Props>()

provide('assetDateLabel', 'Date of Investment')
provide('amountLabel', 'Commitment')
provide('assetRestrictions', ASSET_TYPES_LIST)
provide('valuePlaceholder', 'Investment value')
provide('valueLabel', 'Valuation')

const wizzardRef = useTemplateRef('wizzardRef')

const assetsBunchStore = useAssetsBunchStore()

const isNoDrawdown = ref(false)

const contributions = ref<TransactionClass[]>([])
const distributions = ref<TransactionClass[]>([])

const title = computed(
  () => `Add Private Fund${isNoDrawdown.value ? ' (No Drawdown)' : ''}`,
)

const transactionType = computed(() =>
  isNoDrawdown.value ? TRANSACTION_TYPE_NO_DRAWDOWN : TRANSACTION_TYPE,
)

const investmentStrategiesList = computed(() => {
  return isNoDrawdown.value
    ? INVESTMENT_STRATEGIES_LIST_NO_DRAWDOWN
    : INVESTMENT_STRATEGIES_LIST
})

const contributionsSummary = computed(() => {
  const count = contributions.value.length
  return count ? `${count} ${count === 1 ? 'item' : 'items'}` : undefined
})

const distributionsSummary = computed(() => {
  const count = distributions.value.length
  return count ? `${count} ${count === 1 ? 'item' : 'items'}` : undefined
})

const additionalSummary = computed(() => {
  return [
    { title: 'Contributions', value: contributionsSummary.value },
    { title: 'Distributions', value: distributionsSummary.value },
  ]
})
provide('additionalSummary', additionalSummary)

const handleInit = () => {
  contributions.value = []
  distributions.value = []
}

const handleBeforeSaveTransaction = async (
  accountId?: string | null,
  assetId?: string | null,
  currencyId?: string | null,
) => {
  // save contributions and distributions
  await Promise.all(
    [...contributions.value, ...distributions.value].map(item => {
      // assign account and asset
      accountId && item.pasteAccountId1(accountId, { check: false })
      assetId && item.pasteAssetId1(assetId, { check: false })
      currencyId && item.pasteAssetId2(currencyId, { check: false })
      return item.store()
    }),
  )
}

const handleBeforeSaveAsset = async (
  accountId: string | null,
  asset: AssetClass,
) => {
  if (!accountId) return
  const account = assetsBunchStore.getElementById(accountId)
  if (!account) return
  addPostfixToAssetTicker(asset, account)
}

const handleStepStatus = (index: number) => {
  switch (index) {
    case 2:
      return contributions.value.length > 0
    case 3:
      return distributions.value.length > 0
    default:
      return false
  }
}

watch(isNoDrawdown, value => {
  contributions.value = []
  wizzardRef.value?.setTransaction((transaction, date, accountId) => {
    transaction.field(TRANSACTION_FIELD.TYPE).value = value
      ? TRANSACTION_TYPE_NO_DRAWDOWN
      : TRANSACTION_TYPE
    transaction.field(TRANSACTION_FIELD.DATE).value = date
    transaction.field(TRANSACTION_FIELD.ENTRIES).value = [
      { account_id: accountId || '', amount: 1, asset_id: '' },
      { account_id: '', amount: value ? -1 : null, asset_id: '' },
    ]
  })
})
</script>
